import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Star, Camera } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { PawField } from "@/components/decor";

export default function WallHallOfFame() {
  useSeo("Pup of the Wall — Hall of Fame", "Every month's winning bulldog from our review photo wall.");
  const [winners, setWinners] = useState(null);
  useEffect(() => { api.get("/pup-of-the-wall/hall").then(({ data }) => setWinners(data.winners)).catch(() => setWinners([])); }, []);
  return (
    <div className="relative overflow-hidden">
      <PawField color="#FFD93D" opacity={0.08} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#FFD93D] text-[#19324A] text-xs font-bold uppercase tracking-[0.18em] px-4 py-1.5"><Trophy className="w-4 h-4" /> Hall of Fame</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#19324A] leading-[1.05] mt-5" data-testid="hall-title">Pup of the Wall 🏆</h1>
        <p className="text-base md:text-lg text-[#19324A]/75 mt-4 max-w-xl">Every month Chewie &amp; Solo pick one bulldog from the review photo wall. These are the legends so far.</p>
        {winners === null ? null : winners.length === 0 ? (
          <div className="mt-10 bg-white rounded-3xl soft-shadow p-8 max-w-lg" data-testid="hall-empty">
            <Camera className="w-8 h-8 text-[#FF6B9D]" />
            <p className="font-display font-bold text-[#19324A] mt-3">No winners yet — be the first!</p>
            <p className="text-sm text-[#19324A]/65 mt-1">Add a photo of your pup to a review (you'll get 10% off for it) and you're in the running.</p>
            <Link to="/shop" className="btn-pink mt-5 inline-flex">Shop & review</Link>
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7" data-testid="hall-grid">
            {winners.map((w, i) => (
              <Link key={`${w.month}-${w.review_id}`} to={`/product/${w.product?.slug || ""}#reviews-section`} className={`group bg-white rounded-[2rem] overflow-hidden soft-shadow card-hover ${i === 0 ? "sm:col-span-2 lg:col-span-2 lg:row-span-2" : ""}`} data-testid={`hall-winner-${i}`}>
                <div className={`relative overflow-hidden ${i === 0 ? "aspect-[4/3]" : "aspect-square"}`}>
                  <img src={w.photo} alt={`${w.author_name}'s pup`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#FFD93D] text-[#19324A] text-[11px] font-bold uppercase tracking-wider px-3 py-1"><Trophy className="w-3.5 h-3.5" /> {w.month}</span>
                  {i === 0 && <span className="absolute top-4 right-4 rounded-full bg-[#FF6B9D] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">Reigning champ</span>}
                </div>
                <div className="p-5">
                  <h2 className="font-display font-bold text-[#19324A] text-base md:text-lg">{w.author_name}'s pup</h2>
                  <p className="text-sm text-[#19324A]/65 mt-1 line-clamp-2">{w.note || `Spotted with ${w.product?.name || "our gear"}.`}</p>
                  <p className="text-xs text-[#19324A]/50 mt-3 inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#FFD93D] text-[#FFD93D]" /> {w.rating} · {w.product?.name}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
